import { useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { calcRegistrationTotal } from "@qgiv/p2p-registration";

const UpdateRegistrationTotal = ({
    registrations,
    categories,
    optionalDonation,
    applyFeeCoverage,
    feeCoverage,
    feeCoverageFlat,
    feeCoveragePercentage,
    discounts,
    storeCart,
    products,
    taxRate,
    // Current Redux Totals
    giftAssistAmount,
    giftsSubtotalAmount,
    prePromoRegistrantTotal,
    prePromoTransTotal,
    registrantSubtotal,
    shippingMethodAmount,
    storeTaxAmount,
    storeSubtotalAmount,
    total,
    updateRegistrationTotals,
}) => {
    // -------------------------------------------------------------------------
    // MARK: Calculates all registration totals and sets them to state
    // -------------------------------------------------------------------------
    const dispatch = useDispatch();
    const getNewTotalsObject = (
        totalNumber,
        feeCoverageAmountNumber,
        giftsSubtotalNumber,
        prePromoTransTotalNumber,
        prePromoRegistrantNumber,
        storeTaxNumber,
        storeSubTotalNumber,
        registrantSubtotalNumber,
    ) => {
        // This object should only contain the totals that have changed.
        // Optional Donations and Shipping Method Amount are intentionally
        // left out because they are being set at a component level.  There
        // is no calculation necessary for them. They are reliant completely
        // user selection, therefore we are already watching for there change.
        const newTotals = {
            ...(feeCoverageAmountNumber !== giftAssistAmount && {
                giftAssistAmount: feeCoverageAmountNumber,
            }),
            ...(giftsSubtotalNumber !== giftsSubtotalAmount && {
                giftsSubtotalAmount: giftsSubtotalNumber,
            }),
            ...(prePromoRegistrantNumber !== prePromoRegistrantTotal && {
                prePromoRegistrantTotal: prePromoRegistrantNumber,
            }),
            ...(prePromoTransTotalNumber !== prePromoTransTotal && {
                prePromoTransTotal: prePromoTransTotalNumber,
            }),
            ...(registrantSubtotalNumber !== registrantSubtotal && {
                registrantSubtotal: registrantSubtotalNumber,
            }),
            ...(storeTaxNumber !== storeTaxAmount && {
                storeTaxAmount: storeTaxNumber,
            }),
            ...(storeSubTotalNumber !== storeSubtotalAmount && {
                storeSubtotalAmount: storeSubTotalNumber,
            }),
            ...(totalNumber !== total && { total: totalNumber }),
        };

        return newTotals;
    };

    useEffect(() => {
        // Prevent bad input from being used in calcTotal function
        // This is necessary for OOC-QG-13476 because the input
        // is no longer a type "currency" and can allow non-number characters.
        // Once 'react-imask' issue with block type masks is resolved, we should
        // be able to remove this check.
        if (Number.isNaN(optionalDonation)) return;
        // Calculates total for the entirety of the registration
        const {
            totalNumber,
            feeCoverageAmountNumber,
            giftsSubtotalNumber,
            prePromoTransTotalNumber,
            prePromoRegistrantNumber,
            storeTaxNumber,
            storeSubTotalNumber,
            registrantSubtotalNumber,
        } = calcRegistrationTotal({
            registrations,
            categories,
            optionalDonation,
            applyFeeCoverage,
            feeCoverage,
            feeCoverageFlat,
            feeCoveragePercentage,
            discounts,
            storeCart,
            products,
            taxRate,
            shippingMethodAmount,
        });

        // Figure out which, if any totals have changed by passing in the
        // newly calculated totals and comparing them to what is in state.
        const newTotalsObject = getNewTotalsObject(
            totalNumber,
            feeCoverageAmountNumber,
            giftsSubtotalNumber,
            prePromoTransTotalNumber,
            prePromoRegistrantNumber,
            storeTaxNumber,
            storeSubTotalNumber,
            registrantSubtotalNumber,
        );

        // We only want to update the state if there is a changed total to update
        if (Object.keys(newTotalsObject).length > 0) {
            dispatch(
                updateRegistrationTotals({
                    totals: {
                        ...newTotalsObject,
                    },
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        registrations,
        categories,
        optionalDonation,
        applyFeeCoverage,
        feeCoverage,
        feeCoverageFlat,
        feeCoveragePercentage,
        discounts,
        storeCart,
        products,
        taxRate,
        shippingMethodAmount,
    ]);

    return null;
};

UpdateRegistrationTotal.propTypes = {
    registrations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    optionalDonation: PropTypes.number.isRequired,
    applyFeeCoverage: PropTypes.bool.isRequired,
    feeCoverage: PropTypes.number.isRequired,
    feeCoverageFlat: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    feeCoveragePercentage: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]).isRequired,
    discounts: PropTypes.shape({
        participants: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
        transaction: PropTypes.shape({}),
    }).isRequired,
    products: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    storeCart: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    taxRate: PropTypes.number.isRequired,
    // Current Redux Totals
    giftAssistAmount: PropTypes.number.isRequired,
    giftsSubtotalAmount: PropTypes.number.isRequired,
    prePromoRegistrantTotal: PropTypes.number.isRequired,
    prePromoTransTotal: PropTypes.number.isRequired,
    registrantSubtotal: PropTypes.number.isRequired,
    shippingMethodAmount: PropTypes.number.isRequired,
    storeTaxAmount: PropTypes.number.isRequired,
    storeSubtotalAmount: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    updateRegistrationTotals: PropTypes.func.isRequired,
};

export default UpdateRegistrationTotal;
